// @file Helper for Whiteboard (tools, colors, etc)
import device from '@@/bits/device'
import { isAppUsing } from '@@/bits/flip'
import { FontNameToFontId } from '@@/bits/font'
import { __ } from '@@/bits/intl'
import { ActionType, getAllShapesInGroup, isGroupShape } from '@@/bits/whiteboard'
import type { TldrawDefaultColorStyleColor, ToolTailwindColorType } from '@@/bits/whiteboard_tools'
import type { WhiteboardTextSize, WhiteboardTool } from '@@/types/whiteboard'
import { useTldrawEditor } from '@@/vuecomposables/useTldrawEditor'
import { useWhiteboardHeadlessTiptapEditor } from '@@/vuecomposables/use_whiteboard_headless_tiptap_editor'
import { useWhiteboardShapeHandlers } from '@@/vuecomposables/whiteboard_shape_handlers'
import type { Content } from '@padlet/whiteboard-tiptap'
import type { TLArrowShapeArrowheadStyle, TLDefaultDashStyle, TLRecord, TLShape, TLShapeId } from 'tldraw'

const MOD_KEY = device.mac ? '⌘' : 'Ctrl+'
const copyRow = {
  icon: 'copy_outline',
  text: __('Copy'),
  eventPayload: ActionType.Copy,
  rightLabel: `${MOD_KEY}C`,
}
const cutRow = {
  icon: 'scissors',
  text: __('Cut'),
  eventPayload: ActionType.Cut,
  rightLabel: `${MOD_KEY}X`,
}
const pasteRow = {
  icon: 'clipboard_outline',
  text: __('Paste'),
  eventPayload: ActionType.Paste,
  rightLabel: `${MOD_KEY}V`,
}
const duplicateRow = {
  icon: 'duplicate_outline',
  text: __('Duplicate'),
  eventPayload: ActionType.Duplicate,
  rightLabel: `${MOD_KEY}D`,
}
const selectAllRow = {
  icon: 'select_all',
  text: __('Select all'),
  eventPayload: ActionType.SelectAll,
  rightLabel: '⇧A',
}
const freezeRow = {
  icon: 'freeze',
  text: __('Freeze'),
  eventPayload: ActionType.ToggleFreeze,
  rightLabel: '⇧F',
}
const unfreezeRow = {
  icon: 'freeze',
  text: __('Unfreeze'),
  eventPayload: ActionType.ToggleFreeze,
}
const sendToFrontRow = {
  icon: 'send_to_front_outline',
  text: __('Send to front'),
  eventPayload: ActionType.SendToFront,
  rightLabel: ']',
}
const sendToBackRow = {
  icon: 'send_to_back_outline',
  text: __('Send to back'),
  eventPayload: ActionType.SendToBack,
  rightLabel: '[',
}
const groupRow = {
  icon: 'group_shapes',
  text: __('Group'),
  eventPayload: ActionType.Group,
  rightLabel: `${MOD_KEY}G`,
}
const ungroupRow = {
  icon: 'ungroup_shapes',
  text: __('Ungroup'),
  eventPayload: ActionType.Ungroup,
  rightLabel: `${MOD_KEY}⇧G`,
}
const deleteRow = {
  icon: 'delete',
  text: __('Delete'),
  eventPayload: ActionType.Delete,
  rightLabel: '⌫',
}
const editPollRow = {
  icon: 'pencil_outline',
  text: __('Edit poll'),
  eventPayload: ActionType.EditPoll,
}
const openPollRow = {
  icon: 'poll_open',
  text: __('Open poll'),
  eventPayload: ActionType.OpenPoll,
}
const closePollRow = {
  icon: 'poll_close',
  text: __('Close poll'),
  eventPayload: ActionType.ClosePoll,
}
const exportPollRow = {
  icon: 'download_outline',
  text: __('Export poll results as CSV'),
  eventPayload: ActionType.ExportPoll,
}

function checkCanEdit(shape: TLShape): boolean {
  const editor = useTldrawEditor().editor
  if (editor == null) return false
  const shapeHandlers = useWhiteboardShapeHandlers(editor)
  return shapeHandlers.checkCanEdit(shape)
}

function mapTldrawColorToWhiteboardColor(
  secondaryMenuItems: WhiteboardTool[],
  tldrawColor: TldrawDefaultColorStyleColor,
): ToolTailwindColorType {
  return secondaryMenuItems.find((item) => item.tldrawToolId === tldrawColor)?.id as ToolTailwindColorType
}

type FillStyle = 'none' | 'semi' | 'solid'

function getFillStyleIconName(fillStyle: FillStyle): string {
  switch (fillStyle) {
    case 'none':
      return 'fill-none-element-modifier'
    case 'semi':
      return 'fill-white-element-modifier'
    case 'solid':
      return 'fill-solid-element-modifier'
    default:
      return ''
  }
}

function getTextSizeLabel(textSize: WhiteboardTextSize | null): string {
  switch (textSize) {
    case 'auto':
      // whiteboard-element-modifier-text-size-label is defined in WhiteboardElementModifier.vue
      return isAppUsing('sandboxTextFormatting')
        ? '<p class="whiteboard-element-modifier-text-size-label"><span>A</span><span>A</span></p>'
        : __('Auto')
    case 's':
    case 's1':
      return __('Small')
    case 'm':
    case 'm1':
    case 'xl':
      // Handle legacy shapes with 'xl' size
      return __('Medium')
    case 'l':
    case 'l1':
      return __('Large')
    default:
      return ''
  }
}

const THEME_FONT_MENU_ITEM_ID = 'themeFont'

function getFontCssClasses(fontId: FontNameToFontId): string {
  return {
    [FontNameToFontId.Inter]: 'font-sans',
    [FontNameToFontId.ShantellSans]: 'font-shantell',
    [FontNameToFontId.CourierPrime]: 'font-courier',
    [FontNameToFontId.Lora]: 'font-serif',
  }[fontId]
}

type ConnectorStyle = [TLArrowShapeArrowheadStyle, TLDefaultDashStyle, TLArrowShapeArrowheadStyle]

function getConnectorStyleIconName(connectorStyle: ConnectorStyle): string {
  const [arrowheadStart, dash, arrowheadEnd] = connectorStyle

  // Connector styles are a combination of three arrow shape props
  // We offer menu items to toggle them in tandem instead of individually
  const connectorStyleStringified = `${arrowheadStart}-${dash}-${arrowheadEnd}`
  switch (connectorStyleStringified) {
    case 'arrow-draw-arrow':
      return 'solid_double'
    case 'none-draw-arrow':
      return 'solid_single'
    case 'none-draw-none':
      return 'solid_none'
    case 'arrow-dashed-arrow':
      return 'dash_double'
    case 'none-dashed-arrow':
      return 'dash_single'
    case 'none-dashed-none':
      return 'dash_none'
    default:
      return ''
  }
}

function isShapeWithRichText(shape: TLShape | TLRecord): boolean {
  if (shape == null) return false
  const shapes = isGroupShape(shape as TLShape) ? getAllShapesInGroup(shape as TLShape) : [shape]

  return shapes.every((currentShape) => currentShape.typeName === 'shape' && 'richText' in currentShape.props)
}

function getTextShapes(shapes: TLShape[]): TLShape[] {
  return shapes.filter((shape) => isShapeWithRichText(shape))
}

function updateRichTextOfShapes(
  shapes: TLShape[],
  operation: (payload: { shape: TLShape; content: Content }) => Content,
): void {
  const editor = useTldrawEditor().editor
  if (editor == null || shapes.length === 0) return

  const textShapes = getTextShapes(shapes)
  if (textShapes.length === 0) return

  const richTextMap = new Map<TLShapeId, Content>()
  textShapes.forEach((shape) => {
    if (!('richText' in shape.props)) return
    richTextMap.set(shape.id, operation({ shape, content: shape.props.richText as Content }))
  })

  const shapesToUpdate = shapes
    .filter((shape) => richTextMap.has(shape.id))
    .map((shape) => ({
      id: shape.id,
      type: shape.type,
      props: { richText: richTextMap.get(shape.id) },
    }))

  editor.updateShapes(shapesToUpdate)
}

function setTextShapesBold(shapes: TLShape[]): void {
  updateRichTextOfShapes(shapes, ({ content }) => useWhiteboardHeadlessTiptapEditor(content).setBold())
}

function unsetTextShapesBold(shapes: TLShape[]): void {
  updateRichTextOfShapes(shapes, ({ content }) => useWhiteboardHeadlessTiptapEditor(content).unsetBold())
}

function setTextShapesItalic(shapes: TLShape[]): void {
  updateRichTextOfShapes(shapes, ({ content }) => useWhiteboardHeadlessTiptapEditor(content).setItalic())
}

function unsetTextShapesItalic(shapes: TLShape[]): void {
  updateRichTextOfShapes(shapes, ({ content }) => useWhiteboardHeadlessTiptapEditor(content).unsetItalic())
}

function setTextShapesStrike(shapes: TLShape[]): void {
  updateRichTextOfShapes(shapes, ({ content }) => useWhiteboardHeadlessTiptapEditor(content).setStrike())
}

function unsetTextShapesStrike(shapes: TLShape[]): void {
  updateRichTextOfShapes(shapes, ({ content }) => useWhiteboardHeadlessTiptapEditor(content).unsetStrike())
}

function unsetTextShapesColor(shapes: TLShape[]): void {
  updateRichTextOfShapes(shapes, ({ content }) => useWhiteboardHeadlessTiptapEditor(content).unsetColor())
}

function toggleTextShapesBulletList(shapes: TLShape[]): void {
  updateRichTextOfShapes(shapes, ({ content }) => useWhiteboardHeadlessTiptapEditor(content).toggleBulletList())
}

function toggleTextShapesOrderedList(shapes: TLShape[]): void {
  updateRichTextOfShapes(shapes, ({ content }) => useWhiteboardHeadlessTiptapEditor(content).toggleOrderedList())
}

enum TextFormattingOption {
  Bold = 'bold',
  Italic = 'italic',
  Strikethrough = 'strikethrough',
  BulletedList = 'bulletedList',
  NumberedList = 'numberedList',
}

const textFormattingOptions = {
  [TextFormattingOption.Bold]: {
    id: TextFormattingOption.Bold,
    iconName: 'bold',
    label: __('Bold'),
    tldrawToolId: '',
  },
  [TextFormattingOption.Italic]: {
    id: TextFormattingOption.Italic,
    iconName: 'italic',
    label: __('Italic'),
    tldrawToolId: '',
  },
  [TextFormattingOption.Strikethrough]: {
    id: TextFormattingOption.Strikethrough,
    iconName: 'strikethrough',
    label: __('Strikethrough'),
    tldrawToolId: '',
  },
  [TextFormattingOption.BulletedList]: {
    id: TextFormattingOption.BulletedList,
    iconName: 'bulleted_list',
    label: __('Bulleted list'),
    tldrawToolId: '',
  },
  [TextFormattingOption.NumberedList]: {
    id: TextFormattingOption.NumberedList,
    iconName: 'numbered_list',
    label: __('Numbered list'),
    tldrawToolId: '',
  },
}

export {
  checkCanEdit,
  closePollRow,
  copyRow,
  cutRow,
  deleteRow,
  duplicateRow,
  editPollRow,
  exportPollRow,
  freezeRow,
  getConnectorStyleIconName,
  getFillStyleIconName,
  getFontCssClasses,
  getTextSizeLabel,
  groupRow,
  isShapeWithRichText,
  mapTldrawColorToWhiteboardColor,
  MOD_KEY,
  openPollRow,
  pasteRow,
  selectAllRow,
  sendToBackRow,
  sendToFrontRow,
  setTextShapesBold,
  setTextShapesItalic,
  setTextShapesStrike,
  TextFormattingOption,
  textFormattingOptions,
  THEME_FONT_MENU_ITEM_ID,
  toggleTextShapesBulletList,
  toggleTextShapesOrderedList,
  unfreezeRow,
  ungroupRow,
  unsetTextShapesBold,
  unsetTextShapesColor,
  unsetTextShapesItalic,
  unsetTextShapesStrike,
}
export type { ConnectorStyle, FillStyle }
